import googleTagManager from '@analytics/google-tag-manager';
import hubSpotPlugin from '@analytics/hubspot';
import Analytics from 'analytics';
import { authFragmentStripFailed } from '@/Domain/utils/authCallbackParams';

const IS_DEV_ENV = process.env.NODE_ENV === 'development';
// dd_env is stamped into the SPA shell's #frontend-config script tag at render
// time. Dev/staging branch deploys are still built with NODE_ENV=production, so
// keying behaviour off NODE_ENV alone would treat them as prod and block on GTM
// callbacks that never fire (because the dev shell does not load GTM).
function readDdEnv(): string {
  if (typeof document === 'undefined') return 'unknown';
  const tag = document.getElementById('frontend-config');
  if (!tag?.textContent) return 'unknown';
  try {
    return JSON.parse(tag.textContent).dd_env ?? 'unknown';
  } catch {
    return 'unknown';
  }
}
const IS_PROD_ENV = process.env.NODE_ENV === 'production' && readDdEnv() === 'prod';

const GTM_EVENT_TIMEOUT_MS = 2000;

const devOnlyPlugins = [
  {
    name: 'logger',
    page: ({ payload }: { payload: unknown }) => {
      console.info('analytics-page', payload);
    },
    track: ({ payload }: { payload: unknown }) => {
      console.info('analytics-track', payload);
    },
    identify: ({ payload }: { payload: unknown }) => {
      console.info('analytics-identify', payload);
    },
  },
];

const prodOnlyPlugins = [
  googleTagManager({
    containerId: 'GTM-TQJKXSZ7',
  }),
  hubSpotPlugin({
    portalId: '26630649',
  }),
];

// A login nonce or passkey setup token could not be stripped from the URL, so no tag may load this page.
const urlSecretExposed = (): boolean => authFragmentStripFailed();

const plugins = urlSecretExposed()
  ? []
  : [...(IS_DEV_ENV ? devOnlyPlugins : []), ...(IS_PROD_ENV ? prodOnlyPlugins : [])];

/**
 * Track an event and navigate after all GTM tags have fired.
 *
 * Use this instead of `track()` + callback when the page navigates or reloads
 * after the event. Pushes directly to GTM's dataLayer with `eventCallback` so
 * the navigation waits for tags (e.g. Google Ads conversion pixels) to complete.
 * Also pushes to HubSpot's queue which survives page navigation independently.
 *
 * Falls back to navigating after `GTM_EVENT_TIMEOUT_MS` if a tag hangs.
 */
export function trackAndNavigate(
  eventName: string,
  properties: Record<string, unknown>,
  navigate: () => void,
): void {
  // In non-production environments GTM is not loaded, so eventCallback/eventTimeout
  // would never fire. Navigate immediately to avoid hanging the page.
  if (!IS_PROD_ENV || urlSecretExposed()) {
    if (IS_DEV_ENV) {
      console.info('analytics-trackAndNavigate', { eventName, properties });
    }
    navigate();
    return;
  }

  // HubSpot: push to _hsq queue (survives navigation independently)
  const _hsq = (window._hsq = window._hsq || []);
  _hsq.push(['trackCustomBehavioralEvent', { name: eventName, properties }]);

  // Both eventCallback AND eventTimeout are executed by gtm.js itself — if the
  // container is blocked (adblock, corporate proxy) or hasn't loaded, neither
  // ever fires, so navigation must not depend on them.
  let navigated = false;
  const navigateOnce = () => {
    if (navigated) return;
    navigated = true;
    navigate();
  };
  const fallbackTimer = setTimeout(navigateOnce, GTM_EVENT_TIMEOUT_MS);

  // GTM: push to dataLayer with eventCallback to wait for all tags to complete.
  // Properties are nested under `eventProperties` so they get replaced atomically
  // on each push — GTM's dataLayer is merge-based and flat properties would leak
  // across subsequent events.
  const dataLayer = (window.dataLayer = window.dataLayer || []);
  dataLayer.push({
    event: eventName,
    eventProperties: { ...properties },
    eventCallback: () => {
      clearTimeout(fallbackTimer);
      navigateOnce();
    },
    eventTimeout: GTM_EVENT_TIMEOUT_MS,
  });
}

/**
 * Push a fire-and-forget event to GTM dataLayer and HubSpot.
 *
 * Use this for events where no navigation follows. For events that
 * precede a navigation/reload, use `trackAndNavigate()` instead.
 */
export function pushGtmEvent(eventName: string, properties: Record<string, unknown> = {}): void {
  if (!IS_PROD_ENV || urlSecretExposed()) {
    if (IS_DEV_ENV) {
      console.info('analytics-pushGtmEvent', { eventName, properties });
    }
    return;
  }

  // Properties are nested under `eventProperties` so they get replaced atomically
  // on each push — GTM's dataLayer is merge-based and flat properties would leak
  // across subsequent events.
  const dataLayer = (window.dataLayer = window.dataLayer || []);
  dataLayer.push({ event: eventName, eventProperties: { ...properties } });

  const _hsq = (window._hsq = window._hsq || []);
  _hsq.push(['trackCustomBehavioralEvent', { name: eventName, properties }]);
}

export default Analytics({
  app: 'quickchat-app',
  debug: IS_DEV_ENV,
  plugins,
});
